import { FC } from 'react'

import { parseISO } from 'date-fns'
import { groupBy, isEqual, sortBy } from 'lodash'

import { EventReportItem } from '../../../../components/EventReportItem'
import { ReportsTabWrapper } from '../ReportsTabWrapper/ReportsTabWrapper'

import { TEventRegisteredUsers } from '../../../../../../../models/event-registered-users/event-registered-users.interface'
import {
  IBreakRegistrationByDay,
  IEventInterface,
} from '../../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'
import {
  FeesTypeItem,
  RegistrationFeesType,
} from '../../../../../../../models/event-fees/event-fees.interface'
import { IEventScheduleInterface } from '../../../../../../../models/event-scheduling/event-scheduling.interface'
import { IRiderTeamMemberInterface } from '../../../../../../../models/rider-team-member/riderTeamMember.interface'
import { IReports } from '../../ManageClinicReportsRoot'
import { IHorseData } from '../../../../../../../models/horse/horse.interface'

import { createEntriesMasterListPdf } from '../../../../../../../templates/pdf/reports/event-reports/entries-master-list'
import { createOrderOfGoByClassPdf } from '../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-class'
import { createOrderOfGoByRiderPdf } from '../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-rider'
import { createClassScheduleByDayAndFacility } from '../../../../../../../templates/pdf/reports/event-reports/entries-class-schedule-by-day-facility'
import {
  createOrderOfGoByDayAndRing,
  GroupedRows,
} from '../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-day-ring'

import { convertDateString, getDayOfWeek } from '../../../../../../../helpers/pdf'
import { getRideTime, sortScheduling } from '../../../../../../../helpers/time'
import useToasterHelper from '../../../../../../../helpers/ToasterHelper'
import {
  getEntryName,
  getHorseNames,
  getSortedAndUnique,
  getTeamMemberName,
  sortByOrder,
} from '../../../../ManageClinicNOtherOperationsTab/data/operations-tab-utils'
import {
  getAllRegistrationFees,
  getGroupFeesByDayAndFacility,
} from '../../../../../../../helpers/registrationFees'
import { getFeeTime } from '../../../../../../../helpers/schedule'

import { MESSAGES_CONST } from '../../../../../../../const/messages-const'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../../../../types/payment'

interface EventReportsTabProps {
  event: IEventInterface | null
  registeredUsersByDay: IRegistrationByDayInterface[]
  registrationFees: any
  eventRegisteredUsers: TEventRegisteredUsers[] | null
  riderTeamMembers: IRiderTeamMemberInterface[]
  activeOption: IReports
  scheduleBy: 'class' | 'rider'
  customScheduling?: IEventScheduleInterface['custom'] | null
  setActiveOption: (value: IReports) => void
  horses: IHorseData[] | null
}

const EventReportsReport: FC<EventReportsTabProps> = ({
  event,
  registeredUsersByDay,
  registrationFees,
  riderTeamMembers,
  scheduleBy,
  activeOption,
  customScheduling,
  setActiveOption,
  horses,
}) => {
  const toastFunctions = useToasterHelper()

  const onDownloadEntriesPdf = async () => {
    const rows: string[][] = []
    const allHorses = new Set()
    const allTrainers: Set<string> = new Set()
    const uniqueRegisteredUsersByDay: IRegistrationByDayInterface[] = []

    try {
      // get unique registered users by day
      registeredUsersByDay?.forEach((registeredUserByDay) => {
        const hasRider = uniqueRegisteredUsersByDay.find(
          (item) =>
            item.registrationDocId === registeredUserByDay.registrationDocId &&
            item.riderId === registeredUserByDay.riderId &&
            isEqual(item.horsesSelected, registeredUserByDay.horsesSelected)
        )

        if (!hasRider) uniqueRegisteredUsersByDay.push(registeredUserByDay)
      })

      // create rows
      uniqueRegisteredUsersByDay?.forEach((registeredUserByDay) => {
        const coachName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Coach')
        const trainerName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Trainer')
        const horseNames = getHorseNames(horses, registeredUserByDay.horsesSelected)

        const sameClasses = registeredUsersByDay?.filter(
          (registered) =>
            registered.riderId === registeredUserByDay.riderId &&
            isEqual(registered.horsesSelected, registeredUserByDay.horsesSelected)
        )

        const classes = sameClasses?.map((registration) => getEntryName(registration))

        const row = [
          `**${registeredUserByDay.backNumber}**`,
          `${registeredUserByDay.riderName} | ^^${coachName}^^`,
          `${horseNames.join(', ')} | ^^${trainerName}^^`,
          `${classes?.join(' | ')}`,
          `${sameClasses?.filter((rider) => !rider.isScratched)?.length || 0}`,
        ]

        rows.push(row)

        if (trainerName) allTrainers.add(trainerName)
        registeredUserByDay?.horsesSelected?.forEach((horse) => allHorses.add(horse.id))
      })

      const countRiders = new Set(registeredUsersByDay?.map((rider) => rider.riderId))
      const countActiveClasses = registeredUsersByDay?.filter((rider) => !rider.isScratched) || []

      if (event?.id && uniqueRegisteredUsersByDay?.[0]) {
        await createEntriesMasterListPdf(
          event,
          rows,
          countRiders.size,
          allHorses.size,
          allTrainers.size,
          countActiveClasses.length
        )
        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByDayAndRing = async () => {
    try {
      const eventBreaks = event?.breaks || []
      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees)
      const groupFeesByDayAndFacility = getGroupFeesByDayAndFacility(flattenedFees)

      const uniqueRegistrationsByDay: IRegistrationByDayInterface[] = []

      registeredUsersByDay?.forEach((registeredUserByDay) => {
        const isRiderIdExist = uniqueRegistrationsByDay.some(
          (item) =>
            item.uuid === registeredUserByDay.uuid &&
            item.riderId === registeredUserByDay.riderId &&
            isEqual(item.horsesSelected, registeredUserByDay.horsesSelected)
        )

        if (!isRiderIdExist) uniqueRegistrationsByDay.push(registeredUserByDay)
      })

      const groupedByDayFacility: Record<
        string,
        (IBreakRegistrationByDay & { fee?: FeesTypeItem })[]
      > = {}

      Object.entries(groupFeesByDayAndFacility).forEach(([groupKey, { fees }]) => {
        const feeUuids = fees.map(({ uuid }) => uuid)

        // All registrations that belong to any class on the day
        let registrationsForGroup =
          uniqueRegistrationsByDay?.filter(
            (registration: IRegistrationByDayInterface) =>
              registration.uuid &&
              feeUuids.includes(registration.uuid) &&
              (registration.paymentType !== PAYMENT_TYPE.DEFAULT ||
                registration.paymentStatus !== PAYMENT_STATUS.DEFAULT)
          ) || []

        registrationsForGroup = registrationsForGroup.map(
          (registration: IRegistrationByDayInterface, index: number) => {
            const matchingFee = flattenedFees.find(
              (feeItem: RegistrationFeesType) => feeItem.name === registration.registrationByDayName
            )

            return {
              ...registration,
              riderName: registration.riderName,
              order: registration.order ?? index + 1,
              fee: matchingFee,
            }
          }
        )

        const breaksForGroup = eventBreaks.filter((brk) => brk.groupKey === groupKey) || []
        let combinedList = [...registrationsForGroup, ...breaksForGroup]
        groupedByDayFacility[groupKey] = sortByOrder(combinedList)
      })

      // grouped data to the format needed for PDF
      const groupedData: GroupedRows[] = []

      Object.entries(groupedByDayFacility).forEach(([groupKey, registrations]) => {
        // Parse day and facility from the group key
        const [dateStr, facility] = groupKey.split('_')
        const date = parseISO(dateStr)
        const dayName = getDayOfWeek(date)
        const rows: string[][] = []

        registrations.forEach((registrationByDay) => {
          if (registrationByDay.isBreakDrag) {
            rows.push([registrationByDay.time || '', 'Drag Break', ''])
            return
          }

          const backNumber = registrationByDay.backNumber ?? 0
          const trainerName = getTeamMemberName(riderTeamMembers, registrationByDay, 'Trainer')
          const entryName = getEntryName(registrationByDay)
          const horseNames = getHorseNames(horses, registrationByDay.horsesSelected)

          if (!registrationByDay.fee) return

          const displayTime = registrationByDay?.orderOfGoScratched
            ? 'Dropped'
            : registrationByDay?.rideTime
              ? getRideTime(registrationByDay.rideTime)
              : 'Not Set'

          rows.push([
            displayTime || 'Not set',
            `${entryName}<spacer>#${backNumber}`,
            `${registrationByDay.riderName} • ${horseNames} • ${trainerName}`,
          ])
        })

        if (rows?.[0]) {
          groupedData.push({
            day: dayName,
            date: date,
            facility: facility,
            rows: rows,
          })
        }
      })

      if (event?.id && groupedData?.[0]) {
        await createOrderOfGoByDayAndRing(event, groupedData)
        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByRiderPdf = async () => {
    const rows: string[][] = []

    try {
      const groupedByRegistrationByDayName = groupBy(registeredUsersByDay, 'registrationByDayName')

      Object.keys(groupedByRegistrationByDayName)?.forEach((registrationByDayName) => {
        getSortedAndUnique(groupedByRegistrationByDayName[registrationByDayName]).forEach(
          (registeredUserByDay) => {
            const coachName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Coach')
            const trainerName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Trainer')

            const registrationFee = getAllRegistrationFees(
              registrationFees?.registrationFees
            )?.find((fee) => fee.uuid === registeredUserByDay.uuid)

            // If no matching registration fee - skip iteration
            if (!registrationFee) return

            const entryName = getEntryName(registeredUserByDay)
            const horseNames = getHorseNames(horses, registeredUserByDay.horsesSelected)
            const displayTime = getFeeTime(
              [
                ...registeredUsersByDay.filter((item) => item.uuid === registeredUserByDay.uuid),
                ...(event?.breaks?.filter(
                  (item) => item.registrationByDayUuid === registeredUserByDay.uuid
                ) || []),
              ],
              registeredUserByDay,
              registrationFee,
              scheduleBy
            )

            const row = [
              `${registeredUserByDay.riderName} | ^^${coachName}^^ ^^•^^ ^^#${registeredUserByDay.backNumber ?? 0}^^`,
              `${horseNames} | ^^${trainerName}^^`,
              `${entryName}`,
              `${getDayOfWeek(registrationFee.startDate)} | ^^${convertDateString(registrationFee.startDate)}^^`,
              `${displayTime}`,
            ]

            rows.push(row)
          }
        )
      })

      const countRiders = new Set(registeredUsersByDay?.map((rider) => rider.riderId))

      if (event?.id && registeredUsersByDay?.[0]) {
        await createOrderOfGoByRiderPdf(event, sortBy(rows), countRiders.size)
        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByClassPdf = async () => {
    const rows: { [key: string]: string[][] } = {}
    try {
      const groupedByRegistrationByDayName = groupBy(registeredUsersByDay, 'registrationByDayName')

      Object.keys(groupedByRegistrationByDayName)?.forEach((registrationByDayName) => {
        getSortedAndUnique(groupedByRegistrationByDayName[registrationByDayName]).forEach(
          (registeredUserByDay) => {
            const coachName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Coach')
            const trainerName = getTeamMemberName(riderTeamMembers, registeredUserByDay, 'Trainer')
            const backNumber = registeredUserByDay.backNumber ?? 0

            const registrationFee = getAllRegistrationFees(
              registrationFees?.registrationFees
            )?.find((fee) => fee.uuid === registeredUserByDay.uuid)

            if (!registrationFee) return

            const entryName = getEntryName(registeredUserByDay)
            const horseNames = getHorseNames(horses, registeredUserByDay.horsesSelected)
            const displayTime = getFeeTime(
              [
                ...registeredUsersByDay.filter((item) => item.uuid === registeredUserByDay.uuid),
                ...(event?.breaks?.filter(
                  (item) => item.registrationByDayUuid === registeredUserByDay.uuid
                ) || []),
              ],
              registeredUserByDay,
              registrationFee,
              scheduleBy
            )

            const row = [
              `${registeredUserByDay.riderName} | ^^${coachName}^^`,
              `${horseNames} | ^^${trainerName}^^`,
              `${entryName}<spacer>#${backNumber}`,
              `${displayTime}`,
            ]

            if (!rows[registrationByDayName]) rows[registrationByDayName] = []
            rows[registrationByDayName].push(row)
          }
        )
      })

      if (event?.id && registeredUsersByDay?.[0]) {
        await createOrderOfGoByClassPdf(event, rows)
        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadClassScheduleByDayAndFacilityPdf = async () => {
    try {
      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees)
      const schedules = [...(flattenedFees || []), ...(customScheduling || [])]
      const groupFeesByDayAndFacility = getGroupFeesByDayAndFacility(schedules)

      //  grouped data to the format needed for PDF
      const groupedData: GroupedRows[] = []

      Object.entries(groupFeesByDayAndFacility).forEach(([groupKey, { fees }]) => {
        // parse day and facility from the group key
        const [dateStr, facilityName] = groupKey.split('_')
        const date = parseISO(dateStr)
        const dayName = getDayOfWeek(date)
        const rows: string[][] = []

        sortScheduling(fees).forEach((fee) => {
          const displayTime = `${fee.startTimeHours}:${fee.startTimeMinutes} ${fee.startTimeFormat}`
          rows.push([`${displayTime} | ^^${dayName}^^`, facilityName, fee.name ?? 'N/A'])
        })

        if (rows?.[0]) {
          groupedData.push({
            day: dayName,
            date: date,
            facility: facilityName,
            rows: rows,
          })
        }
      })

      // grouped data by date
      groupedData.sort((a, b) => a.date.getTime() - b.date.getTime())

      if (event?.id && groupedData?.[0]) {
        await createClassScheduleByDayAndFacility(event, groupedData)
        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return (
    <ReportsTabWrapper
      title="Event Reports"
      description="Export and print event reports "
      activeOption={activeOption}
      setActiveOption={setActiveOption}
    >
      <div className="flex flex-wrap gap-x-[2%] gap-y-5">
        <EventReportItem
          isAvailable
          title="Entries"
          description="master list"
          onClick={onDownloadEntriesPdf}
        />
        {scheduleBy === 'class' && (
          <EventReportItem
            isAvailable
            title="Order of Go"
            description="by class"
            onClick={onDownloadOrderOfGoByClassPdf}
          />
        )}
        {scheduleBy === 'rider' && (
          <>
            <EventReportItem
              isAvailable
              title="Order of Go"
              description="by day and ring"
              onClick={onDownloadOrderOfGoByDayAndRing}
            />
          </>
        )}
        <EventReportItem
          isAvailable
          title="Order of Go"
          description="by rider"
          onClick={onDownloadOrderOfGoByRiderPdf}
        />
        <EventReportItem title="Show Schedule" description="by day" onClick={async () => {}} />
        <EventReportItem
          isAvailable
          title="Class schedule"
          description="by day and facility"
          onClick={onDownloadClassScheduleByDayAndFacilityPdf}
        />
      </div>
    </ReportsTabWrapper>
  )
}

export default EventReportsReport
